import { useEffect, useState } from "react";
import { useLanguage } from "../../i18n/LanguageContext";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

function toDateStr(d) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildFormFromBooking(booking) {
  if (booking) {
    return {
      service_id: booking.service_id, date: booking.date, start_time: booking.start_time,
      customer_name: booking.customer_name, customer_email: booking.customer_email,
      customer_phone: booking.customer_phone, notes: booking.notes || "",
    };
  }
  return { service_id: "", date: toDateStr(new Date()), start_time: "", customer_name: "", customer_email: "", customer_phone: "", notes: "" };
}

export const BookingFormDialog = ({ open, onOpenChange, booking, onSaved }) => {
  const { t, lang } = useLanguage();
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ service_id: "", date: "", start_time: "", customer_name: "", customer_email: "", customer_phone: "", notes: "" });
  const [slots, setSlots] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) api.get("/admin/services").then(({ data }) => setServices(data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    setForm(buildFormFromBooking(booking));
    setError("");
  }, [booking, open]);

  useEffect(() => {
    if (form.service_id && form.date) {
      api.get("/availability/slots", { params: { service_id: form.service_id, date: form.date } })
        .then(({ data }) => setSlots(data.slots))
        .catch(() => setSlots([]));
    }
  }, [form.service_id, form.date]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target?.value ?? e });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      if (booking) {
        await api.put(`/admin/bookings/${booking.id}`, form);
      } else {
        await api.post("/admin/bookings", form);
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="booking-form-dialog" className="bg-[#141414] border-white/10 text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {booking ? t("admin.bookings.editBooking") : t("admin.bookings.newBooking")}
            {booking?.appointment_number && <span className="text-gold ml-2 text-sm font-normal">#{booking.appointment_number}</span>}
          </DialogTitle>
          <DialogDescription>{t("admin.bookings.formDescription")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-white/70 text-sm">{t("admin.bookings.service")}</Label>
            <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v, start_time: "" })}>
              <SelectTrigger data-testid="booking-form-service-select" className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10 text-white">
                {services.map((s) => <SelectItem key={s.id} value={s.id}>{lang === "nl" ? s.name_nl : s.name_en} (€{s.price})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/70 text-sm">{t("admin.bookings.date")}</Label>
              <Input type="date" data-testid="booking-form-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value, start_time: "" })} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" />
            </div>
            <div>
              <Label className="text-white/70 text-sm">{t("admin.bookings.time")}</Label>
              <Select value={form.start_time} onValueChange={(v) => setForm({ ...form, start_time: v })}>
                <SelectTrigger data-testid="booking-form-time-select" className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10 text-white max-h-64">
                  {form.start_time && !slots.some((s) => s.start === form.start_time) && <SelectItem value={form.start_time}>{form.start_time}</SelectItem>}
                  {slots.map((s) => <SelectItem key={s.start} value={s.start}>{s.start}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-white/70 text-sm">{t("booking.form.name")}</Label>
            <Input data-testid="booking-form-name-input" value={form.customer_name} onChange={set("customer_name")} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-white/70 text-sm">{t("booking.form.email")} <span className="text-white/40">({lang === "nl" ? "optioneel" : "optional"})</span></Label>
              <Input type="email" data-testid="booking-form-email-input" value={form.customer_email} onChange={set("customer_email")} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" />
            </div>
            <div>
              <Label className="text-white/70 text-sm">{t("booking.form.phone")} <span className="text-white/40">({lang === "nl" ? "optioneel" : "optional"})</span></Label>
              <Input data-testid="booking-form-phone-input" value={form.customer_phone} onChange={set("customer_phone")} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" />
            </div>
          </div>
          <div>
            <Label className="text-white/70 text-sm">{t("admin.bookings.notes")}</Label>
            <Textarea data-testid="booking-form-notes-input" value={form.notes} onChange={set("notes")} className="bg-[#1A1A1A] border-white/10 text-white rounded-none mt-1.5" />
          </div>
          {error && <p data-testid="booking-form-error" className="text-red-400 text-sm">{error}</p>}
          <Button type="submit" data-testid="booking-form-submit" disabled={submitting} className="w-full bg-gold text-white hover:bg-gold-hover rounded-none disabled:opacity-60">
            {submitting ? t("common.saving") : t("common.save")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
